import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Award, CheckCircle2 } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { useMyEnrolledCourses } from '@/hooks/useCourses'
import { useCertificateData } from '@/services/certificates.service'
import { formatBrDate } from '@/components/super-admin/badges/format'
import { CertificateRenderer } from './CertificateRenderer'

/**
 * Tab "Certificados" do UserProfile.
 *
 * Lista os cursos completados pelo usuário (course_enrollments.completedAt
 * != null). Clicar em um curso busca o pacote de dados via
 * useCertificateData(courseId) e renderiza o certificado.
 *
 * Suporta deep link via querystring `?tab=certificados&courseId=X` —
 * usado pelo CTA do CourseFeedback (logo após terminar curso).
 */
export function CertificatesTab() {
  const [searchParams] = useSearchParams()
  const initialCourseId = searchParams.get('courseId')
  const [selectedCourseId, setSelectedCourseId] = useState<number | undefined>(
    initialCourseId ? Number(initialCourseId) : undefined
  )

  const { data: myCourses, isLoading: coursesLoading } = useMyEnrolledCourses()
  const completedCourses = useMemo(
    () => (myCourses ?? []).filter((c) => c.completedAt),
    [myCourses]
  )

  // Pré-seleciona o 1º curso completo se não veio nada via querystring
  const effectiveCourseId =
    selectedCourseId ?? (completedCourses.length > 0 ? completedCourses[0].id : undefined)

  const certificateQuery = useCertificateData(effectiveCourseId)

  if (coursesLoading) {
    return <Skeleton className="h-64 w-full" />
  }

  if (completedCourses.length === 0) {
    return (
      <Card className="glass-card border-border/50">
        <CardContent className="py-12 text-center">
          <Award className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
          <p className="text-sm font-medium">Nenhum certificado ainda</p>
          <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
            Conclua um curso pra desbloquear seu primeiro certificado. Cada curso terminado vira um
            certificado disponível pra download aqui.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Lista de cursos completos */}
      <div className="flex flex-wrap gap-2">
        {completedCourses.map((c) => {
          const isActive = effectiveCourseId === c.id
          return (
            <Button
              key={c.id}
              variant={isActive ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCourseId(c.id)}
              className="gap-2"
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span className="font-normal">{c.title}</span>
              {c.completedAt && (
                <span className="text-xs opacity-70">{formatBrDate(c.completedAt)}</span>
              )}
            </Button>
          )
        })}
      </div>

      {/* Render do certificado selecionado */}
      {certificateQuery.isLoading ? (
        <Skeleton className="h-[500px] w-full" />
      ) : certificateQuery.isError ? (
        <Card className="glass-card border-border/50">
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            Não foi possível carregar o certificado. Tente novamente.
          </CardContent>
        </Card>
      ) : certificateQuery.data ? (
        <CertificateRenderer data={certificateQuery.data} />
      ) : null}
    </div>
  )
}
